import React, { useState, useEffect, useCallback, useRef } from "react";
import { CImage } from "@coreui/react";
import "@coreui/coreui/dist/css/coreui.min.css";
import { Box, Button, Typography, IconButton, Stack } from "@mui/material";
import HeaderStyles from "../../../styles/PublicPage/header";
import { typo } from "../../../assets/fonts/font";
import { AWS_URL } from "../../../configs/constants";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { IBannerItems } from "../../../models/IBanner";
import colorConfigs from "../../../configs/colorConfigs";

interface BannerSliderProps {
  items: IBannerItems[];
}

const BannerSlider: React.FC<BannerSliderProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 5000);
  }, [items.length]);

  const navigate = useCallback(
    (direction: "next" | "prev") => {
      setCurrentIndex((prev) =>
        direction === "next"
          ? (prev + 1) % items.length
          : (prev - 1 + items.length) % items.length
      );
      resetTimer();
    },
    [items.length, resetTimer]
  );

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const renderContent = (item: IBannerItems) => (
    <>
      {/* Title (Centered) */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 2,
          width: "50%",
        }}
      >
        <Typography
          sx={{
            ...typo.headline,
            color: item.text_color,
            fontSize: { xs: "26px", sm: "22px", md: "48px" },
          }}
        >
          {item.text}
        </Typography>
      </Box>

      {/* Button (Bottom) */}
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          textAlign: "center",
          width: '100%',
        }}
      >
        <Button
          color="inherit"
          sx={{ ...HeaderStyles.BannerButton, ...typo.button , width:'50%'}}
          href={item.btn_url}
        >
          {item.btn_text}
        </Button>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "0 0 20px 20px",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {items.length > 0 && (
        <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              transition: "transform 0.5s ease-in-out",
              transform: `translateX(-${currentIndex * 100}%)`,
              height: "100%",
            }}
          >
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{ minWidth: "100%", height: "100%", position: "relative" }}
              >
                <CImage
                  className="d-block w-100 h-100"
                  style={{
                    objectFit: "cover",
                    objectPosition: "center",
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                  src={`${AWS_URL}/${item.asset}`}
                  alt={`Banner ${index + 1}`}
                />
              </Box>
            ))}
          </Box>

          {/* Render Content (Title and Button) */}
          {items[currentIndex] && renderContent(items[currentIndex])}

          {/* Navigation Arrows */}
          <IconButton
            onClick={() => navigate("prev")}
            sx={{
              position: "absolute",
              left: 20,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "var(--color-overlay-black-30)",
              color: colorConfigs.secondary,
              zIndex: 2,
              "&:hover": {
                bgcolor: "var(--color-overlay-black-50)",
              },
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => navigate("next")}
            sx={{
              position: "absolute",
              right: 20,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "var(--color-overlay-black-30)",
              color: colorConfigs.secondary,
              zIndex: 2,
              "&:hover": {
                bgcolor: "var(--color-overlay-black-50)",
              },
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>

          {/* Dots Indicator */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 2,
            }}
          >
            {items.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor:
                    index === currentIndex
                      ? colorConfigs.secondary
                      : "var(--color-overlay-white-50)",
                  cursor: "pointer",
                }}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BannerSlider;